import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { activateLoader, disableLoader } from '@Redux/actions/ui-trigger';
import { DOCUMENT_ID_ICON_DUAL_TONE, CHEVRON_LEFT_ICON, CLOSE_NEW_ICON } from '@Images';
import { CommonButton, StepHeader, ModalFactory, PDFViewer } from '@Components';
import {
  E_SIGN_IN_SCREEN,
  screenSettings,
  totalSteps,
  NIUM_FOREX_CURRENT_SCREEN,
  TRANSACTION_ID
} from '../../utils/constants';
import { NF_CALLBACK_URLS, NF_SCREENS } from '../../root/screenConstants';
import { Col, EmptyPDFContainer, FormContainer, ParentContainer, Row } from '../../styles';
import CancelPaymentModal from '../../utils/cancel-payment-modal';
import { IESignInScreen, INiumForexCMSData } from '../../utils/interface';
import { changeNiumForexScreen, storeTransactionIdAction } from '@Redux/actions/nium-forex';
import {
  getA2Form,
  getCMSDataForA2FormMessage,
  getESignInRedirectionUrl,
  getESignInStatus
} from '../../utils/util';
import { IStore } from '../../../../store.type';
import {
  FLOW_TYPE_TRANSACTION,
  NF_ESIGNIN_EVENT
} from '../../../../utils/constants/eventsConstants';
import { logEvent } from '@Utils/events/logUserEvents';
import { LOG_TYPE } from '@Utils/events/eventsConstants';

type IhtmlData = {
  bufferData: string;
  isReferral: boolean;
};

const ESignInForm: React.FC<IESignInScreen> = (props) => {
  const {
    activateLoader,
    disableLoader,
    getA2Form,
    getESignInRedirectionUrl,
    getESignInStatus,
    dispatch,
    transactionId
  } = props;

  const [htmlData, setHtmlData] = useState<IhtmlData>();
  const [cmsData, setCMSData] = useState<INiumForexCMSData>();
  const stepperData = screenSettings[NF_SCREENS.e_sign_in as keyof typeof screenSettings];

  useEffect(() => {
    const currentScreen = localStorage.getItem(NIUM_FOREX_CURRENT_SCREEN);
    const currentUrl = window.location.href;
    if (
      currentScreen === NF_SCREENS.e_sign_in &&
      currentUrl?.split('/')?.includes(NF_CALLBACK_URLS.eSignInCallbackUrl)
    ) {
      // User is redirected user from idfy esignin flow
      fetchESignInStatus();
    } else fetchESignInPDF();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchESignInStatus = async () => {
    const lsTransactionId = Number(localStorage.getItem(TRANSACTION_ID) || 0);
    dispatch(storeTransactionIdAction(lsTransactionId));
    dispatch(activateLoader());
    const eSignInStatus = await dispatch(getESignInStatus(lsTransactionId));
    if (eSignInStatus) {
      localStorage.removeItem(NIUM_FOREX_CURRENT_SCREEN);
      localStorage.removeItem(TRANSACTION_ID);
      dispatch(changeNiumForexScreen(NF_SCREENS.transaction_summary));
    } else {
      fetchESignInPDF(lsTransactionId);
    }
    dispatch(disableLoader());
  };

  const fetchESignInPDF = async (lsTransactionId?: number) => {
    dispatch(activateLoader());
    const response = await dispatch(
      getA2Form(transactionId ? transactionId : lsTransactionId || 0)
    );
    if (response) {
      setHtmlData(response);
      if (response?.isReferral) {
        const res = await getCMSDataForA2FormMessage();
        setCMSData(res?.labels);
      }
    }
    dispatch(disableLoader());
  };

  const onCancelPayment = async () => {
    // @ts-ignore
    return await ModalFactory.builder?.(
      (onClose: any) => (
        <CancelPaymentModal
          onContinue={() => onClose(true)}
          onConfirm={() => {
            dispatch(changeNiumForexScreen(NF_SCREENS.send_money));
            onClose(true);
          }}
          postPan={true}
        />
      ),
      'center-modal insta-modal',
      true
    );
  };

  const handleBackNavigation = () => {
    dispatch(changeNiumForexScreen(NF_SCREENS.select_bank));
  };

  const handleProceed = () => {
    dispatch(changeNiumForexScreen(NF_SCREENS.transaction_summary));
  };

  const logEvents = () => {
    logEvent(
      NF_ESIGNIN_EVENT,
      FLOW_TYPE_TRANSACTION,
      { pageName: NF_SCREENS.e_sign_in },
      LOG_TYPE.CLEVER_TAP
    );
  };

  const initiateESignIn = async () => {
    dispatch(activateLoader());
    const response = await dispatch(getESignInRedirectionUrl(transactionId));
    if (response) {
      logEvents();
      localStorage.setItem(TRANSACTION_ID, transactionId?.toString());
      const redirectUrl = response?.sign_url;
      if (redirectUrl) {
        localStorage.setItem(NIUM_FOREX_CURRENT_SCREEN, NF_SCREENS.e_sign_in);
        window.open(redirectUrl, '_self');
      }
    }
    dispatch(disableLoader());
  };

  const getPDFContainer = () => {
    if (htmlData?.bufferData === '') return <EmptyPDFContainer />;
    return (
      <div>
        {htmlData && (
          <PDFViewer htmlContent={htmlData.bufferData} height="500px" pdfTitle="E-SignIn Form" />
        )}
      </div>
    );
  };

  return (
    <ParentContainer padding={[30, 0, 48, 0]}>
      <div className="navigationContainer">
        <img
          className="backButton"
          src={CHEVRON_LEFT_ICON}
          alt="back"
          onClick={handleBackNavigation}
        />
        <img className="closeButton" src={CLOSE_NEW_ICON} alt="close" onClick={onCancelPayment} />
      </div>
      <StepHeader
        heading={htmlData?.isReferral ? E_SIGN_IN_SCREEN.heading2 : E_SIGN_IN_SCREEN.heading}
        headerLogo={DOCUMENT_ID_ICON_DUAL_TONE}
        stepNumber={stepperData?.stepNumber}
        totalSteps={totalSteps}
        subHeading={
          htmlData?.isReferral
            ? cmsData?.a2FormMessage || E_SIGN_IN_SCREEN.isReferralSubHeading
            : E_SIGN_IN_SCREEN.subHeading
        }
      />
      <FormContainer marginTop={45}>
        <div>
          <Row padding={[13, 0]}>
            <Col width={450}>{getPDFContainer()}</Col>
          </Row>
          <Row padding={[24, 0, 0, 0]}>
            <Col width={450}>
              <CommonButton
                text={
                  htmlData?.isReferral ? E_SIGN_IN_SCREEN.buttonProceed : E_SIGN_IN_SCREEN.buttonMsg
                }
                width={450}
                height={46}
                onClick={htmlData?.isReferral ? handleProceed : initiateESignIn}
              />
            </Col>
          </Row>
        </div>
      </FormContainer>
    </ParentContainer>
  );
};

const mapStateToProps = (reduxState: IStore) => {
  const {
    rootReducer: {
      niumForexReducer: {
        transactionDetails: { transactionId }
      }
    }
  } = reduxState;

  return {
    transactionId
  };
};

export default connect(mapStateToProps, (dispatch: any) => {
  return {
    changeNiumForexScreen,
    getA2Form,
    getESignInRedirectionUrl,
    getESignInStatus,
    activateLoader,
    disableLoader,
    dispatch
  };
  // @ts-ignore
})(ESignInForm);
